import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { UiSelectOption } from '../select/ui-select.component';

@Component({
  selector: 'ui-autocomplete',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ui-autocomplete.component.html',
  styleUrl: './ui-autocomplete.component.scss',
})
export class UiAutocompleteComponent {
  @Input() options: UiSelectOption[] = [];

  /** Currently selected value (must match an option's value). Empty = nothing selected. */
  @Input()
  get value(): string {
    return this._value;
  }
  set value(v: unknown) {
    this._value = v === null || v === undefined ? '' : String(v);
    if (!this.focused) {
      this.query = this.selectedLabel;
    }
  }
  private _value = '';

  @Input() placeholder = 'Type to search…';
  @Input() emptyMessage = 'No matches';
  @Input() disabled = false;
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Input() fullWidth = false;
  @Input() clearable = true;
  @Input() ariaLabel?: string;
  @Input() className = '';

  /** Maximum number of suggestions shown. */
  @Input() maxResults = 50;

  @Output() valueChange = new EventEmitter<string>();

  @ViewChild('inputEl') inputEl?: ElementRef<HTMLInputElement>;

  query = '';
  open = false;
  focused = false;
  highlightIndex = -1;

  constructor(private host: ElementRef<HTMLElement>) {}

  ngOnChanges(): void {
    if (!this.focused) {
      this.query = this.selectedLabel;
    }
  }

  get selectedLabel(): string {
    if (!this._value) return '';
    return this.options.find((o) => o.value === this._value)?.label ?? '';
  }

  get filteredOptions(): UiSelectOption[] {
    const q = this.query.trim().toLowerCase();
    const source = this.options.filter((o) => !o.disabled);
    if (!q) {
      return source.slice(0, this.maxResults);
    }
    return source
      .filter((o) => o.label.toLowerCase().includes(q))
      .slice(0, this.maxResults);
  }

  get computedInputClasses(): string {
    const classes = ['input', 'input-bordered', 'w-full', 'text-base', 'md:text-sm'];
    if (this.size === 'sm') classes.push('input-sm');
    else if (this.size === 'lg') classes.push('input-lg');
    return [...classes, this.className].join(' ').trim();
  }

  get containerClasses(): string {
    return this.fullWidth ? 'relative w-full' : 'relative inline-block';
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.query = target.value;
    this.open = true;
    this.highlightIndex = this.filteredOptions.length > 0 ? 0 : -1;

    // Typing clears the selection if the typed text doesn't match the selected label.
    if (this._value && this.query !== this.selectedLabel) {
      this._value = '';
      this.valueChange.emit('');
    }
  }

  onFocus(): void {
    this.focused = true;
    this.open = true;
    // Show all suggestions on focus rather than the selected label.
    if (this._value) {
      this.query = '';
    }
    this.highlightIndex = this.filteredOptions.findIndex((o) => o.value === this._value);
    if (this.highlightIndex < 0 && this.filteredOptions.length > 0) {
      this.highlightIndex = 0;
    }
  }

  onBlur(): void {
    // Slight delay so click on an option still registers.
    setTimeout(() => {
      this.focused = false;
      this.open = false;
      // Restore the input to reflect the current selection.
      this.query = this.selectedLabel;
    }, 120);
  }

  onKeyDown(event: KeyboardEvent): void {
    const list = this.filteredOptions;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.open = true;
      if (list.length === 0) return;
      this.highlightIndex = (this.highlightIndex + 1) % list.length;
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.open = true;
      if (list.length === 0) return;
      this.highlightIndex = (this.highlightIndex - 1 + list.length) % list.length;
    } else if (event.key === 'Enter') {
      if (this.highlightIndex >= 0 && list[this.highlightIndex]) {
        event.preventDefault();
        this.selectOption(list[this.highlightIndex]);
      }
    } else if (event.key === 'Escape') {
      if (this.open) {
        event.preventDefault();
        this.open = false;
        this.query = this.selectedLabel;
      }
    }
  }

  selectOption(option: UiSelectOption): void {
    if (option.disabled) return;
    this._value = option.value;
    this.query = option.label;
    this.open = false;
    this.focused = false;
    this.valueChange.emit(option.value);
    this.inputEl?.nativeElement?.blur();
  }

  onClear(event: Event): void {
    event.stopPropagation();
    if (!this._value && !this.query) return;
    this._value = '';
    this.query = '';
    this.valueChange.emit('');
    this.inputEl?.nativeElement?.focus();
  }

  trackByValue(_index: number, option: UiSelectOption): string {
    return option.value;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.open) return;
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.open = false;
      this.focused = false;
      this.query = this.selectedLabel;
    }
  }
}

